import React, { useEffect } from 'react';

interface SecurityLayerProps {
  contentRef: React.RefObject<HTMLDivElement>;
  allowHighlights?: boolean; // Nova prop para controlar se highlights são permitidos
}

const SecurityLayer: React.FC<SecurityLayerProps> = ({ contentRef, allowHighlights = true }) => {
  useEffect(() => {
    // Enhanced security with controlled text selection for highlights
    const preventScreenCapture = () => {
      // Check if running on mobile/Capacitor
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
        window.innerWidth <= 768;

      if (contentRef.current) {
        if (allowHighlights) {
          // Permitir seleção de texto para highlights em todas as plataformas
          contentRef.current.style.userSelect = 'text';
          contentRef.current.style.webkitUserSelect = 'text';
          (contentRef.current.style as any).webkitTouchCallout = 'default';
        } else {
          // Bloquear completamente se highlights não são permitidos
          contentRef.current.style.userSelect = 'none';
          contentRef.current.style.webkitUserSelect = 'none';
          (contentRef.current.style as any).webkitTouchCallout = 'none';
        }
        contentRef.current.style.pointerEvents = 'auto';
      }

      // Add CSS for controlled selection and security
      const style = document.createElement('style');
      style.textContent = `
        /* Controle global de seleção baseado na funcionalidade de highlights */
        ${allowHighlights ? `
          /* Quando highlights estão habilitados - permitir seleção no conteúdo */
          .chapter-content, .chapter-content * {
            -webkit-user-select: text !important;
            -moz-user-select: text !important;
            -ms-user-select: text !important;
            user-select: text !important;
            -webkit-touch-callout: default !important;
          }
          
          /* Melhorar seleção em mobile para highlights */
          @media (max-width: 768px), (pointer: coarse) {
            .chapter-content, .chapter-content * {
              -webkit-user-select: text !important;
              -moz-user-select: text !important;
              -ms-user-select: text !important;
              user-select: text !important;
              -webkit-touch-callout: default !important;
              -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
            }
          }
        ` : `
          /* Quando highlights estão desabilitados - bloquear seleção */
          .chapter-content, .chapter-content * {
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            user-select: none !important;
            -webkit-touch-callout: none !important;
          }
        `}
        
        /* Sempre permitir seleção em highlights existentes para deletar */
        .highlight-mark {
          -webkit-user-select: text !important;
          -moz-user-select: text !important;
          -ms-user-select: text !important;
          user-select: text !important;
          -webkit-touch-callout: default !important;
        }
        
        /* Bloquear seleção em todo o resto da interface */
        body, body *:not(.chapter-content):not(.chapter-content *):not(.highlight-mark) {
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
          -webkit-touch-callout: none !important;
        }
        
        /* Highlight styling */
        .text-highlight {
          background-color: var(--highlight-color, #ffeb3b);
          border-radius: 2px;
          cursor: pointer;
          position: relative;
        }
        
        /* Indicar que highlights são clicáveis */
        .highlight-mark:hover {
          opacity: 0.8;
          box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
        }
        
        /* Add noise texture to make OCR and screenshots less useful */
        .chapter-content::before {
          content: "";
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          opacity: 0.03; /* Reduced opacity to not interfere with text selection */
          pointer-events: none;
          z-index: 1;
          background-image: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAMAAAAp4XiDAAAAUVBMVEWFhYWDg4N3d3dtbW17e3t1dXWBgYGHh4d5eXlzc3OLi4ubm5uVlZWPj4+NjY19fX2JiYl/f39ra2uRkZGZmZlpaWmXl5dvb29xcXGTk5NnZ2c8TV1mAAAAG3RSTlNAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEAvEOwtAAAFVklEQVR4XpWWB67c2BUFb3g557T/hRo9/WUMZHlgr4Bg8Z4qQgQJlHI4A8SzFVrapvmTF9O7dmYRFZ60YiBhJRCgh1FYhiLAmdvX0CzTOpNE77ME0Zty/nWWzchDtiqrmQDeuv3powQ5ta2eN0FY0InkqDD73lT9c9lEzwUNqgFHs9VQce3TVClFCQrSTfOiYkVJQBmpbq2L6iZavPnAPcoU0dSw0SUTqz/GtrGuXfbyyBniKykOWQWGqwwMA7QiYAxi+IlPdqo+hYHnUt5ZPfnsHJyNiDtnpJyayNBkF6cWoYGAMY92U2hXHF/C1M8uP/ZtYdiuj26UdAdQQSXQErwSOMzt/XWRWAz5GuSBIkwG1H3FabJ2OsUOUhGC6tK4EMtJO0ttC6IBD3kM0ve0tJwMdSfjZo+EEISaeTr9P3wYrGjXqyC1krcKdhMpxEnt5JetoulscpyzhXN5FRpuPHvbeQaKxFAEB6EN+cYN6xD7RYGpXpNndMmZgM5Dcs3YSNFDHUo2LGfZuukSWyUYirJAdYbF3MfqEKmjM+I2EfhA94iG3L7uKrR+GdWD73ydlIB+6hgref1QTlmgmbM3/LeX5GI1Ux1RWpgxpLuZ2+I+IjzZ8wqE4nilvQdkUdfhzI5QDWy+kw5Wgg2pGpeEVeCCA7b85BO3F9DzxB3cdqvBzWcmzbyMiqhzuYqtHRVG2y4x+KOlnyqla8AoWWpuBoYRxzXrfKuILl6SfiWCbjxoZJUaCBj1CjH7GIaDbc9kqBY3W/Rgjda1iqQcOJu2WW+76pZC9QG7M00dffe9hNnseupFL53r8F7YHSwJWUKP2q+k7RdsxyOB11n0xtOvnW4irMMFNV4H0uqwS5ExsmP9AxbDTc9JwgneAT5vTiUSm1E7BSflSt3bfa1tv8Di3R8n3Af7MNWzs49hmauE2wP+ttrq+AsWpFG2awvsuOqbipWHgtuvuaAE+A1Z/7gC9hesnr+7wqCwG8c5yAg3AL1fm8T9AZtp/bbJGwl1pNrE7RuOX7PeMRUERVaPpEs+yqeoSmuOlokqw49pgomjLeh7icHNlG19yjs6XXOMedYm5xH2YxpV2tc0Ro2jJfxC50ApuxGob7lMsxfTbeUv07TyYxpeLucEH1gNd4IKH2LAg5TdVhlCafZvpskfncCfx8pOhJzd76bJWeYFnFciwcYfubRc12Ip/ppIhA1/mSZ/RxjFDrJC5xifFjJpY2Xl5zXdguFqYyTR1zSp1Y9p+tktDYYSNflcxI0iyO4TPBdlRcpeqjK/piF5bklq77VSEaA+z8qmJTFzIWiitbnzR794USKBUaT0NTEsVjZqLaFVqJoPN9ODG70IPbfBHKK+/q/AWR0tJzYHRULOa4MP+W/HfGadZUbfw177G7j/OGbIs8TahLyynl4X4RinF793Oz+BU0saXtUHrVBFT/DnA3ctNPoGbs4hRIjTok8i+algT1lTHi4SxFvONKNrgQFAq2/gFnWMXgwffgYMJpiKYkmW3tTg3ZQ9Jq+f8XN+A5eeUKHWvJWJ2sgJ1Sop+wwhqFVijqWaJhwtD8MNlSBeWNNWTa5Z5kPZw5+LbVT99wqTdx29lMUH4OIG/D86ruKEauBjvH5xy6um/Sfj7ei6UUVk4AIl3MyD4MSSTOFgSwsH/QJWaQ5as7ZcmgBZkzjjU1UrQ74ci1gWBCSGHtuV1H2mhSnO3Wp/3fEV5a+4wz//6qy8JxjZsmxxy5+4w9CDNJY09T072iKG0EnOS0arEYgXqYnXcYHwjTtUNAcMelOd4xpkoqiTYICWFq0JSiPfPDQdnt+4/wuqcXY47QILbgAAAABJRU5ErkJggg==");
        }
      `;
      document.head.appendChild(style);

      // Enhanced anti-copy protection
      const preventCopyPaste = (e: KeyboardEvent) => {
        // Sempre bloquear copy, paste, select all
        if (e.ctrlKey || e.metaKey) {
          if (e.key === 'c' || e.key === 'v' || e.key === 'a' || e.key === 'x') {
            e.preventDefault();
            return false;
          }
        }

        // Prevent PrintScreen key
        if (e.key === 'PrintScreen') {
          e.preventDefault();
          return false;
        }

        // Prevent Ctrl+P (Print), Ctrl+S (Save)
        if (e.ctrlKey && (e.key === 'p' || e.key === 's')) {
          e.preventDefault();
          return false;
        }

        // Prevent Ctrl+Shift+S (Save as)
        if (e.ctrlKey && e.shiftKey && e.key === 's') {
          e.preventDefault();
          return false;
        }

        return true;
      };

      // Prevent context menu - MAS permitir para highlights se necessário
      const preventContextMenu = (e: MouseEvent) => {
        const target = e.target as HTMLElement;

        // Se não estamos permitindo highlights, sempre bloquear
        if (!allowHighlights) {
          e.preventDefault();
          return false;
        }

        // Se permitimos highlights, verificar se clicou em highlight para permitir menu personalizado
        if (target.classList.contains('highlight-mark')) {
          // Você pode personalizar isso para mostrar um menu de contexto customizado
          // Por agora, vamos bloquear também para manter a segurança
          e.preventDefault();
          return false;
        }

        // Para todo o resto, bloquear context menu
        e.preventDefault();
        return false;
      };

      // Prevent devtools opening
      const preventDevTools = (e: KeyboardEvent) => {
        if (
          (e.keyCode === 123) || // F12
          (e.ctrlKey && e.shiftKey && e.keyCode === 73) || // Ctrl+Shift+I
          (e.ctrlKey && e.keyCode === 85) // Ctrl+U
        ) {
          e.preventDefault();
          return false;
        }
      };

      // Prevent copy events - SEMPRE bloquear copy/cut/paste para manter segurança
      const preventCopy = (e: ClipboardEvent) => {
        e.preventDefault();
        return false;
      };

      // Prevent drag and drop - nova proteção
      const preventDrag = (e: DragEvent) => {
        e.preventDefault();
        return false;
      };

      document.addEventListener('contextmenu', preventContextMenu);
      document.addEventListener('keydown', preventCopyPaste);
      document.addEventListener('keydown', preventDevTools);
      document.addEventListener('copy', preventCopy);
      document.addEventListener('cut', preventCopy);
      document.addEventListener('paste', preventCopy);
      document.addEventListener('dragstart', preventDrag);
      document.addEventListener('drop', preventDrag);

      // Return cleanup function
      return () => {
        if (style.parentNode) {
          document.head.removeChild(style);
        }
        document.removeEventListener('contextmenu', preventContextMenu);
        document.removeEventListener('keydown', preventCopyPaste);
        document.removeEventListener('keydown', preventDevTools);
        document.removeEventListener('copy', preventCopy);
        document.removeEventListener('cut', preventCopy);
        document.removeEventListener('paste', preventCopy);
        document.removeEventListener('dragstart', preventDrag);
        document.removeEventListener('drop', preventDrag);
      };
    };

    // Only apply these restrictions on the chapter page
    const cleanup = preventScreenCapture();

    return cleanup;
  }, [contentRef, allowHighlights]);

  return null; // This is a behavior-only component
};

export default SecurityLayer;